import { Box, ButtonGroup, Grid, Typography, useTheme } from "@mui/material";
import CopyrightStatement from "components/CopyrightStatement/CopyrightStatement";
import DownloadableImage from "components/DownloadableImage/DownloadableImage";
import Header from "components/Header/Header";
import ActionButton from "components/styled/ActionButton";
import CenteredColumnGrid from "components/styled/CenteredColumnGrid";
import React, { useContext, useEffect, useRef, useState } from "react";
import { exportComponentAsPNG } from "react-component-export-image";
import { PAGES } from "utils/constants";
import {
  handleRemovePeriod,
  handleReplacePeriod,
  handleReplaceSpaces,
} from "utils/helpers"; // Consolidated imports
import { PageContext } from "utils/PageContext";
import { handleCurrentDate, handleCurrentTime } from "utils/timeUtils";
import { UserContext } from "utils/UserContext";
import WhatsNextDialog from "components/WhatsNextDialog/WhatsNextDialog";
import handleConnectionLevel from "utils/connectionUtils"; // For badge info

function FinalScore() {
  const [openWhatsNext, setOpenWhatsNext] = useState(false);
  const handleOpenWhatsNext = () => setOpenWhatsNext(true);
  const handleCloseWhatsNext = () => setOpenWhatsNext(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const isDownloadable = useRef(false);

  const handleImageDownloadable = () => {
    isDownloadable.current = true;
  };

  const {
    selectedScoreType,
    shareableImgRef,
    setCurrentStep,
    handleResetData,
    user,
    selectedAlbum,
    connectionScore,
  } = useContext(UserContext);

  const { setPage } = useContext(PageContext);

  const mainTheme = useTheme();

  const currentDate = handleCurrentDate();
  const currentTime = handleCurrentTime();
  const connectionLevel = handleConnectionLevel(connectionScore); // Get badge info

  const handleExportImg = async () => {
    try {
      exportComponentAsPNG(shareableImgRef, {
        fileName: `COMPScore-${handleReplaceSpaces(
          handleRemovePeriod(user.firstName)
        )}-${handleReplaceSpaces(
          handleRemovePeriod(selectedAlbum.name)
        )}-${handleReplaceSpaces(
          handleRemovePeriod(selectedAlbum.mainArtist)
        )}-${selectedScoreType}-${handleReplacePeriod(connectionScore)}-${
          currentDate.file
        }-${currentTime.file}`,
      });
    } catch (err) {
      throw new Error(`File Save Error: ${err}`);
    }
  };

  return (
    <>
      <Header />
      <CenteredColumnGrid
        container
        sx={{
          paddingTop: "calc(2rem + 100px)",
        }}
      >
        <Grid item>
          <Box
            component="main"
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              textAlign: "center",
              paddingBottom: "5rem",
            }}
          >
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              spacing={4}
            >
              {/* Score type display */}
              <Grid item sm={8}>
                <Typography
                  component="p"
                  variant="directions"
                  sx={{
                    textTransform: "uppercase",
                  }}
                >
                  Share your{" "}
                  <Box
                    component="span"
                    sx={{
                      textDecoration: "underline 1px",
                      fontSize: "inherit",
                      margin: "1rem 0",
                    }}
                  >
                    {selectedScoreType.toLowerCase()}
                  </Box>{" "}
                  COMP Score
                </Typography>
              </Grid>
              {/* Album Name & Artist */}
              <Grid item sm={8}>
                <Box>
                  <Typography
                    sx={{
                      variant: "h6",
                      fontStyle: "italic",
                      fontWeight: 600,
                      display: "flex", // Add flex to center the content
                      justifyContent: "center", // Center horizontally
                      alignItems: "center", // Center vertically
                      margin: "0 auto",
                    }}
                  >
                    {selectedAlbum.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      display: "flex", // Add flex to center the content
                      justifyContent: "center", // Center horizontally
                      alignItems: "center", // Center vertically
                      margin: "0 auto",
                    }}
                  >
                    {selectedAlbum.mainArtist}
                  </Typography>
                </Box>
                <br />
                {/* Artwork */}
                <Box>
                  <img
                    src={selectedAlbum.img} // Assuming this is the artwork URL
                    alt={`Album artwork for ${selectedAlbum.name}`}
                    style={{ width: "250px", height: "250px" }}
                  />
                </Box>
                {/* Score and Badge */}
                {/* Badge display */}
                <Box
                  sx={{
                    width: "20vw",
                    height: "20vw",
                    maxWidth: "175px",
                    maxHeight: "175px",
                    minWidth: "125px",
                    minHeight: "125px",
                    backgroundImage: `url(${connectionLevel.imagePath})`,
                    backgroundSize: "cover",
                    display: "flex", // Add flex to center the content
                    justifyContent: "center", // Center horizontally
                    alignItems: "center", // Center vertically
                    margin: "0 auto",
                  }}
                />
                <Typography
                  component="p"
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {`Score: ${connectionScore}`}
                </Typography>

                <Typography
                  component="p"
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >{`Connection Level: ${connectionLevel.title}`}</Typography>
              </Grid>

              {/* Buttons */}
              <Grid item>
                <ButtonGroup
                  size="large"
                  variant="contained"
                  orientation="vertical"
                >
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    item
                    spacing={2}
                  >
                    <Grid item>
                      <ActionButton
                        onClick={() => {
                          handleExportImg();
                          handleOpenWhatsNext();
                          setCurrentStep(
                            (prevCurrentStep) => prevCurrentStep + 1
                          );
                        }}
                        sx={{
                          bgcolor: mainTheme.palette.darkPurplePrimary.main,
                        }}
                      >
                        Download
                      </ActionButton>
                    </Grid>

                    <Grid item>
                      <ActionButton
                        onClick={() => {
                          handleResetData();
                          setCurrentStep(0);
                          setPage(PAGES.SEARCH);
                        }}
                        sx={{
                          bgcolor: mainTheme.palette.darkBluePrimary.main,
                        }}
                      >
                        Do another COMP Score
                      </ActionButton>
                    </Grid>

                    <Grid item>
                      <ActionButton
                        href="https://compinitiative.com/manifesto"
                        sx={{
                          bgcolor: mainTheme.palette.darkPinkPrimary.main,
                        }}
                      >
                        Learn more
                      </ActionButton>
                    </Grid>
                  </Grid>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </CenteredColumnGrid>

      <Box
        sx={{
          opacity: 0,
          position: "absolute",
          top: "-100%",
          left: "50%",
          transform: "translateX(50%)",
        }}
      >
        <DownloadableImage handleImageDownloadable={handleImageDownloadable} />
      </Box>
      <CopyrightStatement />
      <WhatsNextDialog
        open={openWhatsNext}
        handleClose={handleCloseWhatsNext}
      />
    </>
  );
}

export default FinalScore;
